import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Container, CssBaseline } from "@mui/material";
import ProductList from "./components/ProductList";
import ProductForm from "./components/ProductForm";
import CategoryList from "./components/CategoryList";
import CategoryForm from "./components/CategoryForm";
import UserList from "./components/UserList";
import UserForm from "./components/UserForm";
import MediaList from "./components/MediaList";
import MediaForm from "./components/MediaForm";
import HeroSlideList from "./components/HeroSlideList";
import HeroSlideForm from "./components/HeroSlideForm";
import TeamMembers from "./components/TeamMembers";
import CompanyInfo from "./components/CompanyInfo";
import Navigation from "./components/Navigation";
import Login from "./components/Login";
import PrivateRoute from "./components/PrivateRoute";
import { AuthProvider } from "./contexts/AuthContext";
import { validateEnvironment, config } from "./config/environment";
import { log } from "./utils/helpers";

const App: React.FC = () => {
  useEffect(() => {
    // Validate environment variables on app startup
    const isValid = validateEnvironment();

    if (isValid) {
      log("Environment validation passed", "info");
    } else {
      log("Environment validation failed - using default values", "warn");
    }

    // Log app startup information
    log(`Starting ${config.app.name} v${config.app.version}`, "info", {
      apiUrl: config.api.baseUrl,
      debug: config.development.debug,
      environment: process.env.NODE_ENV,
    });
  }, []);

  return (
    <AuthProvider>
      <Router>
        <CssBaseline />
        <Navigation />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />

            {/* Protected Routes */}
            <Route element={<PrivateRoute />}>
              {/* Redirect root to products */}
              <Route path="/" element={<Navigate to="/products" replace />} />

              {/* Product Routes */}
              <Route path="/products" element={<ProductList />} />
              <Route path="/products/add" element={<ProductForm />} />
              <Route path="/products/edit/:id" element={<ProductForm />} />

              {/* Category Routes */}
              <Route path="/categories" element={<CategoryList />} />
              <Route path="/categories/add" element={<CategoryForm />} />
              <Route path="/categories/edit/:id" element={<CategoryForm />} />

              {/* User Routes */}
              <Route path="/users" element={<UserList />} />
              <Route path="/users/add" element={<UserForm />} />
              <Route path="/users/edit/:id" element={<UserForm />} />

              {/* Media Routes */}
              <Route path="/media" element={<MediaList />} />
              <Route path="/media/new" element={<MediaForm />} />
              <Route path="/media/edit/:id" element={<MediaForm />} />

              {/* Hero Slide Routes */}
              <Route path="/hero-slides" element={<HeroSlideList />} />
              <Route path="/hero-slides/new" element={<HeroSlideForm />} />
              <Route path="/hero-slides/edit/:id" element={<HeroSlideForm />} />

              {/* Team Members Routes */}
              <Route path="/team-members" element={<TeamMembers />} />

              {/* Company Info Routes */}
              <Route path="/company-info" element={<CompanyInfo />} />
            </Route>

            {/* Catch all - redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Container>
      </Router>
    </AuthProvider>
  );
};

export default App;
